import { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../supabase'
import YouTubeEmbed, { fmtClock } from './YouTubeEmbed'
import PlanDayComments from './PlanDayComments'
import StackedSheet from './StackedSheet'
import FaceStack from './FaceStack'
import { useReadingSettings } from '../hooks/useReadingSettings'
import { VERSIONS, fetchBibleChapter } from '../hooks/useBibleChapter'

// ── READING PLAN DAY SHEET (member) ───────────────────────────────────────
// Rebuilt per docs/designs/PlanDayRedesign.png (docs/FIX-PLANS.md §3a) — an
// overview (recap cover, progress bar, checklist, Start) plus three
// full-height step pages (Bible reading / Devotional / Reflect) stacked
// over it as a local view-swap, then a Done screen.
//
// Props:
//   plan          — { id, title, summary, ... }
//   days          — this plan's reading_plan_days rows, sorted by day_number
//   follow        — { id, days_completed, last_opened_day } for this user
//   initialDay    — day_number to open on
//   sermon        — the source sermon (Recap clip + "Open full sermon" link)
//   churchName    — for the devotional's "reviewed by [church]" line
//   userId, userChurchId
//   onClose()
//   onOpenSermon()          — close this sheet and open the sermon sheet for `sermon`
//   onOpenBible(book, chapter) — close this sheet (and, in a stacked mount,
//                                the sheet behind it too) and open the Bible
//                                tab at that chapter
//   onFollowChange(follow) — called after days_completed/last_opened_day changes

const GOLD = 'var(--gold)'
const SERIF = "'Source Serif 4', Georgia, serif"
const SANS = "'Plus Jakarta Sans', system-ui, sans-serif"
const PURPLE = '#7C6BC4'

const STEP_ORDER = ['reading', 'devotional', 'reflect']
const STEP_LABEL = { reading: 'Bible reading', devotional: 'Devotional', reflect: 'Reflect' }

function summarizePassages(passages, fallbackRef) {
  if (!Array.isArray(passages) || passages.length === 0) return fallbackRef || ''
  return passages.map(p => {
    const range = (!p.end_verse || p.end_verse === p.start_verse) ? `${p.start_verse}` : `${p.start_verse}-${p.end_verse}`
    return `${p.book} ${p.chapter}:${range}`
  }).join(' and ')
}
function verseCountOf(passages) {
  if (!Array.isArray(passages) || passages.length === 0) return 0
  return passages.reduce((sum, p) => sum + Math.max(1, (p.end_verse || p.start_verse) - p.start_verse + 1), 0)
}
function minutesToRead(text) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean).length
  return Math.max(1, Math.round(words / 200))
}

// Visited-step tracking persists per device (localStorage), not the
// database — docs/FIX-PLANS.md §3a doesn't ask for a schema change for
// this, and "which steps has this device opened" is a reasonable thing to
// lose on a fresh install rather than a new reading_plan_follows column.
function visitedKey(planId, dayNumber) { return `nlc.planDayVisited:${planId}:${dayNumber}` }
function loadVisited(planId, dayNumber) {
  try {
    const raw = localStorage.getItem(visitedKey(planId, dayNumber))
    const arr = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(arr) ? arr : [])
  } catch { return new Set() }
}
function saveVisited(planId, dayNumber, set) {
  try { localStorage.setItem(visitedKey(planId, dayNumber), JSON.stringify([...set])) } catch {}
}

export default function ReadingPlanDaySheet({ plan, days, follow, initialDay, sermon, churchName, userId, userChurchId, onClose, onOpenSermon, onOpenBible, onOpenReminder, onFollowChange, stacked }) {
  const [currentDay, setCurrentDay] = useState(initialDay || 1)
  const [localFollow, setLocalFollow] = useState(follow || { days_completed: [], last_opened_day: initialDay || 1 })
  const [saving, setSaving] = useState(false)
  const [view, setView] = useState('overview') // 'overview' | 'reading' | 'devotional' | 'reflect' | 'done'
  const [visited, setVisited] = useState(() => loadVisited(plan.id, initialDay || 1))
  const [moreOpen, setMoreOpen] = useState(false)
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [nudge, setNudge] = useState(false)
  const [commentCountsByDay, setCommentCountsByDay] = useState({}) // day_number -> count
  const [activityByDay, setActivityByDay] = useState({}) // day_number -> { profiles, total }

  const day = useMemo(() => days.find(d => d.day_number === currentDay) || null, [days, currentDay])
  const completedSet = new Set(localFollow?.days_completed || [])
  const isDone = completedSet.has(currentDay)
  const allDone = completedSet.size >= 5
  // The next day to work on — earliest not-yet-completed, never "missed".
  // Days aren't locked or tied to dates (docs/FIX-PLANS.md §3a/§1h).
  const nextDay = useMemo(() => {
    for (let n = 1; n <= 5; n++) if (!completedSet.has(n)) return n
    return 1
  }, [localFollow]) // eslint-disable-line react-hooks/exhaustive-deps

  // Switching days resets the step view and reloads that day's visited set.
  useEffect(() => {
    setView('overview')
    setVisited(loadVisited(plan.id, currentDay))
  }, [currentDay, plan.id])

  function markVisited(step) {
    setVisited(v => {
      if (v.has(step)) return v
      const next = new Set(v); next.add(step)
      saveVisited(plan.id, currentDay, next)
      return next
    })
  }

  // Non-private answer counts, per day — reading_plan_day_comment_counts
  // already excludes private rows and scopes to the viewer's own church.
  useEffect(() => {
    let cancelled = false
    supabase.rpc('reading_plan_day_comment_counts', { p_plan_id: plan.id }).then(({ data }) => {
      if (cancelled || !Array.isArray(data)) return
      const m = {}
      for (const row of data) m[row.day_number] = Number(row.comment_count) || 0
      setCommentCountsByDay(m)
    })
    return () => { cancelled = true }
  }, [plan.id])

  // Who's finished the current day, from the viewer's own church.
  useEffect(() => {
    let cancelled = false
    supabase.rpc('reading_plan_day_activity', { p_plan_id: plan.id, p_day_number: currentDay, p_limit: 3 }).then(({ data }) => {
      if (cancelled) return
      const rows = Array.isArray(data) ? data : []
      setActivityByDay(m => ({ ...m, [currentDay]: { profiles: rows, total: rows[0]?.total_count || 0 } }))
    })
    return () => { cancelled = true }
  }, [plan.id, currentDay])

  async function goToDay(n) {
    setCurrentDay(n)
    if (!localFollow?.id) return
    const next = { ...localFollow, last_opened_day: n }
    setLocalFollow(next)
    onFollowChange?.(next)
    await supabase.from('reading_plan_follows').update({ last_opened_day: n }).eq('id', localFollow.id)
  }

  async function finishDay() {
    if (!localFollow?.id || saving) return
    setSaving(true)
    const nextCompleted = completedSet.has(currentDay) ? localFollow.days_completed : [...(localFollow.days_completed || []), currentDay]
    const next = { ...localFollow, days_completed: nextCompleted, last_opened_day: currentDay }
    setLocalFollow(next)
    onFollowChange?.(next)
    await supabase.from('reading_plan_follows')
      .update({ days_completed: nextCompleted, last_opened_day: currentDay })
      .eq('id', localFollow.id)
    setSaving(false)
    setView('done')
  }

  function shareDay() {
    const url = `https://thebiblecommunity.co.uk/plan/${plan.id}?day=${currentDay}`
    if (navigator.share) { navigator.share({ title: plan.title, url }).catch(() => {}) }
    else { try { navigator.clipboard.writeText(url) } catch (_e) {} }
  }

  // Leaving sets left_at rather than deleting the row (docs/FIX-PLANS.md
  // §1i) — keeps days_completed so rejoining later picks up where you left
  // off, and lets reading_plan_total_joined/reading_plan_day_activity/the
  // daily reminder exclude you by checking left_at is null.
  async function leavePlan() {
    if (!localFollow?.id || leaving) return
    setLeaving(true)
    await supabase.from('reading_plan_follows').update({ left_at: new Date().toISOString() }).eq('id', localFollow.id)
    setLeaving(false)
    setLeaveConfirmOpen(false)
    onFollowChange?.({ ...localFollow, left_at: new Date().toISOString() })
    onClose?.()
  }

  // Stacked (opened from inside the sermon sheet, not from a page): tapping
  // outside never closes — nudge instead, mirroring FullSermonSheet §3d.
  function onBackdropClick(e) {
    if (e.target !== e.currentTarget) return
    if (!stacked) { onClose?.(); return }
    setNudge(false)
    requestAnimationFrame(() => setNudge(true))
  }

  if (!day) {
    return (
      <div style={S.backdrop} onClick={onBackdropClick}>
        <div style={S.sheet}>
          <div style={{ padding: 20, color: 'var(--stone)', fontSize: 13 }}>This day isn't available.</div>
        </div>
      </div>
    )
  }

  return (
    <div style={S.backdrop} onClick={onBackdropClick}>
      <style>{'@keyframes rpds-nudge{0%{transform:translateY(0)}35%{transform:translateY(-14px)}70%{transform:translateY(3px)}100%{transform:translateY(0)}}'}</style>
      <div style={{ ...S.sheet, animation: nudge ? 'rpds-nudge .42s ease' : 'none' }} onAnimationEnd={() => setNudge(false)}>
        {view === 'overview' && (
          <Overview
            plan={plan} day={day} sermon={sermon} currentDay={currentDay} nextDay={nextDay}
            completedSet={completedSet} isDone={isDone} allDone={allDone}
            visited={visited} commentCount={commentCountsByDay[currentDay] || 0}
            activity={activityByDay[currentDay]}
            moreOpen={moreOpen} setMoreOpen={setMoreOpen}
            onClose={onClose} onShare={shareDay}
            onOpenSermon={onOpenSermon}
            onStart={() => setView(isDone ? 'done' : 'reading')}
            onJump={(step) => setView(step)}
            onJumpDay={goToDay}
            onLeave={() => { setMoreOpen(false); setLeaveConfirmOpen(true) }}
            onRemind={onOpenReminder ? () => onOpenReminder(currentDay, plan.id) : undefined}
          />
        )}
        {(view === 'reading' || view === 'devotional' || view === 'reflect') && (
          <StepFlow
            view={view} setView={setView} day={day} plan={plan} sermon={sermon} churchName={churchName}
            currentDay={currentDay} userId={userId} userChurchId={userChurchId}
            markVisited={markVisited} onOpenSermon={onOpenSermon} onOpenBible={onOpenBible}
            onFinish={finishDay} saving={saving}
            commentCount={commentCountsByDay[currentDay] || 0}
            onOverview={() => setView('overview')}
          />
        )}
        {view === 'done' && (
          <DoneScreen
            plan={plan} day={day} currentDay={currentDay} completedSet={completedSet}
            activity={activityByDay[currentDay]} userChurchId={userChurchId} userId={userId}
            onClose={onClose}
            onNextDay={() => { if (currentDay < 5) goToDay(currentDay + 1); setView('overview') }}
          />
        )}
      </div>

      {leaveConfirmOpen && (
        <>
          <div onClick={() => { if (!leaving) setLeaveConfirmOpen(false) }}
            style={{ position: 'fixed', inset: 0, zIndex: 640, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(2px)' }}/>
          <div role="dialog" aria-modal="true" aria-labelledby="leave-plan-title"
            style={{ position: 'fixed', zIndex: 641, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(360px, calc(100vw - 32px))', background: 'var(--white)', border: '1px solid var(--border-strong)', borderRadius: 16, padding: '22px 22px 18px', fontFamily: SANS, boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}>
            <div id="leave-plan-title" style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', marginBottom: 10 }}>Leave this plan?</div>
            <div style={{ fontSize: 14, color: 'var(--stone)', lineHeight: 1.55, marginBottom: 18 }}>
              It'll stop showing as your plan on This Week. You can join again any time and your progress is kept.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" disabled={leaving} onClick={() => setLeaveConfirmOpen(false)}
                style={{ flex: 1, background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 10, padding: 11, fontSize: 14, fontWeight: 600, color: 'var(--ink)', cursor: leaving ? 'default' : 'pointer', opacity: leaving ? 0.5 : 1, fontFamily: SANS }}>
                Cancel
              </button>
              <button type="button" disabled={leaving} onClick={leavePlan}
                style={{ flex: 1, background: '#F97066', border: 'none', borderRadius: 10, padding: 11, fontSize: 14, fontWeight: 700, color: '#fff', cursor: leaving ? 'default' : 'pointer', opacity: leaving ? 0.7 : 1, fontFamily: SANS }}>
                {leaving ? 'Leaving…' : 'Leave plan'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ── OVERVIEW ───────────────────────────────────────────────────────────────
function Overview({ plan, day, sermon, currentDay, nextDay, completedSet, isDone, allDone, visited, commentCount, activity, moreOpen, setMoreOpen, onClose, onShare, onOpenSermon, onStart, onJump, onJumpDay, onLeave, onRemind }) {
  const passageSummary = summarizePassages(day.passages, day.passage_ref)
  const verseCount = verseCountOf(day.passages)
  const minutes = minutesToRead(day.devotional)
  const rows = [
    { key: 'reading', title: 'Bible reading', sub: [passageSummary, verseCount > 0 ? `${verseCount} verse${verseCount === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ') },
    { key: 'devotional', title: 'Devotional', sub: `${minutes} min read` },
    { key: 'reflect', title: 'Reflect', sub: commentCount > 0 ? `One question · 💬 ${commentCount} answered` : 'One question to think about' },
  ]
  const names = (activity?.profiles || []).map(p => p.full_name).filter(Boolean)
  const activityLine = names.length > 0
    ? `${names.slice(0, 2).join(', ')}${(activity?.total || 0) > names.length ? ` + ${activity.total - names.slice(0, 2).length}` : ''} have finished Day ${currentDay}`
    : null

  return (
    <>
      <div style={S.header}>
        <button onClick={onClose} aria-label="Back" style={S.iconBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6"/></svg>
        </button>
        <div style={S.headerTitle}>{plan.title}</div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={onShare} aria-label="Share" style={S.iconBtn}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M16 6l-4-4-4 4M12 2v14"/></svg>
          </button>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setMoreOpen(o => !o)} aria-label="More" style={S.iconBtn}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h.01M12 12h.01M19 12h.01"/></svg>
            </button>
            {moreOpen && (
              <>
                <div onClick={() => setMoreOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 630 }}/>
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 631, background: 'var(--white)', borderRadius: 14, boxShadow: '0 8px 24px rgba(31,27,22,0.18)', overflow: 'hidden', minWidth: 170 }}>
                  {onRemind && (
                    <button type="button" onClick={() => { setMoreOpen(false); onRemind() }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 16px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: SANS, fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>
                      Remind me…
                    </button>
                  )}
                  <button type="button" onClick={onLeave}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 16px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: SANS, fontSize: 13.5, fontWeight: 600, color: '#C2412D' }}>
                    Leave this plan
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div style={S.scroll}>
        <RecapCover day={day} sermon={sermon} onOpenSermon={onOpenSermon}/>

        <DayTiles completedSet={completedSet} currentDay={currentDay} nextDay={nextDay} onJumpDay={onJumpDay}/>
        {!allDone && currentDay !== nextDay && (
          <div style={{ fontSize: 12.5, fontWeight: 700, color: PURPLE, marginBottom: 14 }}>
            {currentDay > nextDay ? 'Reading ahead' : 'Looking back'} ·{' '}
            <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => onJumpDay(nextDay)}>Back to Day {nextDay}</span>
          </div>
        )}

        <h2 style={S.dayTitle}>{day.title || summarizePassages(day.passages, day.passage_ref)}</h2>

        <div style={S.checklistCard}>
          {rows.map((r, i) => {
            const done = isDone || visited.has(r.key)
            return (
              <button key={r.key} type="button" onClick={() => onJump(r.key)}
                style={{ ...S.checklistRow, borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--border)' }}>
                <span style={{ ...S.checkCircle, ...(done ? S.checkCircleDone : {}) }}>
                  {done && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7"/></svg>}
                </span>
                <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--ink)' }}>{STEP_LABEL[r.key]}</div>
                  {r.sub && <div style={{ fontSize: 12, color: 'var(--stone)', marginTop: 1 }}>{r.sub}</div>}
                </span>
              </button>
            )
          })}
        </div>

        {activityLine && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <FaceStack userIds={(activity?.profiles || []).map(p => p.user_id)} profilesById={Object.fromEntries((activity?.profiles || []).map(p => [p.user_id, p]))} size={24}/>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--stone)' }}>{activityLine}</span>
          </div>
        )}

        {allDone ? (
          <div style={S.completeBanner}>You've completed this plan! 🎉</div>
        ) : (
          <button onClick={onStart} style={S.darkBtn}>
            {isDone ? `Day ${currentDay} done ✓` : visited.size > 0 ? `Continue Day ${currentDay}` : `Start Day ${currentDay}`}
          </button>
        )}
      </div>
    </>
  )
}

function DayProgressBar({ completedSet, currentDay }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)' }}>Day {currentDay} of 5</span>
        {completedSet.size > 0 && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--stone)' }}>{completedSet.size} done</span>}
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        {[1, 2, 3, 4, 5].map(n => (
          <div key={n} style={{
            flex: 1, height: 8, borderRadius: 4,
            background: completedSet.has(n) ? 'var(--ink)' : n === currentDay ? 'var(--white)' : 'var(--cream)',
            boxShadow: n === currentDay && !completedSet.has(n) ? `0 0 0 2px ${PURPLE}` : 'none',
          }}/>
        ))}
      </div>
    </div>
  )
}

// Day picker — 5 tappable tiles (docs/FIX-PLANS.md §3a / designs/PlanCardStatesOpen.png
// panel 8): done = dark fill + gold check, the next unread day = "Today",
// the currently-viewed day always gets the purple selection ring regardless
// of its other state, everything else is a plain numbered tile. No day is
// ever locked — tapping any tile jumps straight to it.
function DayTiles({ completedSet, currentDay, nextDay, onJumpDay }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
      {[1, 2, 3, 4, 5].map(n => {
        const done = completedSet.has(n)
        const isToday = n === nextDay && !done
        const selected = n === currentDay
        return (
          <button key={n} type="button" onClick={() => onJumpDay(n)} style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            borderRadius: 14, padding: '9px 4px', border: 'none', cursor: 'pointer', fontFamily: SANS,
            background: done ? '#1F1B16' : 'var(--white)',
            boxShadow: selected ? `0 0 0 2px ${PURPLE}` : 'none',
          }}>
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.04em', color: done ? 'rgba(255,255,255,0.55)' : 'var(--stone)' }}>DAY {n}</span>
            {done ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7"/></svg>
            ) : isToday ? (
              <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink)' }}>Today</span>
            ) : (
              <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)' }}>{n}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// Recap cover: the day's clip, playing inline from its own stored bounds
// (sermon_ref_id's timestamp_s/end_s) — same mechanics as BibleApp.jsx's
// VerseClipPlayingCard (docs/FIX-SERMON-CLIPS.md §2a): cover thumbnail with
// ▶ before playing, inline YouTubeEmbed once tapped, embed-fail fallback to
// a link-out thumbnail, "Full sermon →" button. Optional and never part of
// the checklist — no clip just means no ▶.
function RecapCover({ day, sermon, onOpenSermon }) {
  const [playing, setPlaying] = useState(false)
  const [ref, setRef] = useState(null)
  const [embedFailed, setEmbedFailed] = useState(false)

  // Fetched eagerly (not gated on `playing`) — the cover's own "FROM
  // SUNDAY" label needs the clip's start time before it's ever tapped.
  useEffect(() => {
    let cancelled = false
    if (!day.sermon_ref_id) return
    supabase.from('sermon_refs').select('*').eq('id', day.sermon_ref_id).maybeSingle()
      .then(({ data }) => { if (!cancelled) setRef(data || null) })
    return () => { cancelled = true }
  }, [day.sermon_ref_id])

  const hasClip = !!(day.sermon_ref_id && day.clip_title)
  const thumbBase = sermon?.youtube_id ? `https://i.ytimg.com/vi/${sermon.youtube_id}` : null
  const bounds = ref ? { start: ref.timestamp_s || 0, end: ref.end_s || (ref.timestamp_s || 0) + 90 } : { start: 0, end: 90 }

  if (!hasClip) {
    return (
      <div style={S.coverPlain}>
        {thumbBase ? <img src={`${thumbBase}/mqdefault.jpg`} alt="" style={S.coverThumbImg}/> : <div style={{ ...S.coverThumbImg, background: 'var(--border)' }}/>}
        <div style={S.coverPlainOverlay}/>
        <div style={S.coverPlainLabel}>{day.title || 'Today\'s reading'}</div>
      </div>
    )
  }

  if (playing && sermon?.youtube_id && !embedFailed) {
    return (
      <div style={S.coverCard}>
        <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: 14, overflow: 'hidden', background: '#000' }}>
          <YouTubeEmbed videoId={sermon.youtube_id} start={bounds.start} end={bounds.end}
            onError={(code) => { if (code === 101 || code === 150) setEmbedFailed(true) }}/>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
          <div style={{ minWidth: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: PURPLE }}>▶ PLAYING · FROM {fmtClock(bounds.start)}</span>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink)', marginTop: 2 }}>"{day.clip_title}"</div>
          </div>
          {onOpenSermon && (
            <button onClick={onOpenSermon} style={S.openSermonLink}>Full sermon →</button>
          )}
        </div>
      </div>
    )
  }

  if (embedFailed) {
    const t = ref?.timestamp_s ? Math.floor(ref.timestamp_s) : 0
    const url = sermon?.youtube_id ? `https://www.youtube.com/watch?v=${sermon.youtube_id}${t ? `&t=${t}s` : ''}` : null
    return (
      <div style={S.coverCard}>
        <a href={url || undefined} target="_blank" rel="noreferrer" style={{ display: 'block', position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: 14, overflow: 'hidden', background: 'var(--border)' }}>
          {thumbBase && <img src={`${thumbBase}/mqdefault.jpg`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>}
        </a>
        <div style={{ fontSize: 12.5, color: 'var(--stone)', marginTop: 8 }}>This clip can't play here — tap to watch on YouTube.</div>
      </div>
    )
  }

  return (
    <button type="button" onClick={() => setPlaying(true)} style={S.coverCardBtn}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: 14, overflow: 'hidden', background: 'var(--border)' }}>
        {thumbBase && <img src={`${thumbBase}/mqdefault.jpg`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>}
        <span style={{ position: 'absolute', left: '50%', top: '50%', width: 52, height: 52, marginLeft: -26, marginTop: -26, borderRadius: 26, background: 'rgba(255,255,255,0.94)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>▶</span>
      </div>
      <div style={{ marginTop: 10, textAlign: 'left' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--gold-ink)', letterSpacing: '0.02em' }}>▶ PLAY RECAP · {fmtClock(bounds.start)} FROM SUNDAY</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', marginTop: 2 }}>"{day.clip_title}"</div>
      </div>
    </button>
  )
}

// ── STEP FLOW (Bible reading → Devotional → Reflect) ───────────────────────
function StepFlow({ view, setView, day, plan, sermon, churchName, currentDay, userId, userChurchId, markVisited, onOpenSermon, onOpenBible, onFinish, saving, commentCount, onOverview }) {
  useEffect(() => { markVisited(view) }, [view]) // eslint-disable-line react-hooks/exhaustive-deps

  const idx = STEP_ORDER.indexOf(view)
  const dragRef = useRef({ x0: null })
  function onPointerDown(e) { dragRef.current.x0 = e.clientX }
  function onPointerUp(e) {
    if (dragRef.current.x0 == null) return
    const dx = e.clientX - dragRef.current.x0
    dragRef.current.x0 = null
    if (Math.abs(dx) < 60) return
    if (dx < 0 && idx < STEP_ORDER.length - 1) setView(STEP_ORDER[idx + 1])
    else if (dx > 0 && idx > 0) {
      if (idx === 0) onOverview()
      else setView(STEP_ORDER[idx - 1])
    }
  }

  const kicker = `${STEP_LABEL[view].toUpperCase()} · ${idx + 1} OF 3`
  const nextLabel = idx < STEP_ORDER.length - 1 ? `Next: ${STEP_LABEL[STEP_ORDER[idx + 1]]} ›` : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }} onPointerDown={onPointerDown} onPointerUp={onPointerUp}>
      <div style={S.stepHeader}>
        <button onClick={idx === 0 ? onOverview : () => setView(STEP_ORDER[idx - 1])} aria-label="Back" style={S.iconBtn}>✕</button>
        <div style={{ flex: 1, display: 'flex', gap: 4, margin: '0 12px' }}>
          {STEP_ORDER.map((s, i) => (
            <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: i < idx ? 'var(--ink)' : i === idx ? PURPLE : 'var(--border-strong)' }}/>
          ))}
        </div>
      </div>
      <div style={S.kicker}>{kicker}</div>

      <div style={{ ...S.scroll, flex: 1 }}>
        {view === 'reading' && <BibleReadingStep day={day} sermon={sermon} onOpenBible={onOpenBible}/>}
        {view === 'devotional' && <DevotionalStep day={day} sermon={sermon} churchName={churchName}/>}
        {view === 'reflect' && (
          <ReflectStep day={day} plan={plan} userId={userId} userChurchId={userChurchId} commentCount={commentCount}
            onFinish={onFinish} saving={saving}/>
        )}
      </div>

      {nextLabel && (
        <div style={{ padding: '0 18px calc(env(safe-area-inset-bottom) + 18px)', flexShrink: 0 }}>
          <button onClick={() => setView(STEP_ORDER[idx + 1])} style={S.darkBtn}>{nextLabel}</button>
        </div>
      )}
    </div>
  )
}

// Fallback for days saved before `passages` existed — best-effort parse of
// "Book Ch:V-V" / "Book Ch:V" out of the free-text passage_ref.
function fallbackPassages(passageRef) {
  const m = String(passageRef || '').match(/^(.+?)\s+(\d+):(\d+)(?:-(\d+))?$/)
  if (!m) return []
  return [{ book: m[1].trim(), chapter: parseInt(m[2], 10), start_verse: parseInt(m[3], 10), end_verse: m[4] ? parseInt(m[4], 10) : parseInt(m[3], 10) }]
}

function BibleReadingStep({ day, sermon, onOpenBible }) {
  const readingSettings = useReadingSettings()
  const [ver, setVer] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nlc.lastPlace') || '{}').ver || 'ESV' } catch { return 'ESV' }
  })
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [blocks, setBlocks] = useState(null) // [{ book, chapter, start_verse, end_verse, verses }] | null = loading
  const [error, setError] = useState('')

  const passages = (Array.isArray(day.passages) && day.passages.length > 0) ? day.passages : fallbackPassages(day.passage_ref)

  useEffect(() => {
    let cancelled = false
    if (passages.length === 0) { setBlocks([]); return }
    setBlocks(null); setError('')
    Promise.all(passages.map(async p => {
      const verses = await fetchBibleChapter(ver, p.book, p.chapter)
      return { ...p, verses: verses.filter(v => v.v >= p.start_verse && v.v <= (p.end_verse || p.start_verse)) }
    })).then(result => { if (!cancelled) setBlocks(result) })
      .catch(e => { if (!cancelled) { setError(e.message || 'Could not load the passage.'); setBlocks([]) } })
    return () => { cancelled = true }
  }, [ver, JSON.stringify(passages)]) // eslint-disable-line react-hooks/exhaustive-deps

  function changeVer(v) {
    setVer(v)
    try {
      const lp = JSON.parse(localStorage.getItem('nlc.lastPlace') || '{}')
      localStorage.setItem('nlc.lastPlace', JSON.stringify({ ...lp, ver: v }))
    } catch {}
  }

  const lastBlock = passages[passages.length - 1]

  return (
    <div style={{ '--scripture-font': readingSettings.cssVars['--scripture-font'], '--scripture-size': readingSettings.cssVars['--scripture-size'], '--scripture-lh': readingSettings.cssVars['--scripture-lh'] }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <button type="button" aria-label="Reading settings" onClick={() => setSettingsOpen(o => !o)}
          style={{ width: 38, height: 38, borderRadius: 19, border: 'none', cursor: 'pointer', fontFamily: SERIF, fontSize: 15, fontWeight: 600, color: 'var(--ink)', background: settingsOpen ? GOLD : 'var(--white)' }}>Aa</button>
      </div>

      {settingsOpen && (
        <div style={S.aaPopover}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--stone)', marginBottom: 6 }}>Text size</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <button onClick={() => readingSettings.setSizeIndex(readingSettings.sizeIndex - 1)} style={S.aaSizeBtn}>A</button>
            <button onClick={() => readingSettings.setSizeIndex(readingSettings.sizeIndex + 1)} style={{ ...S.aaSizeBtn, fontSize: 19 }}>A</button>
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--stone)', marginBottom: 6 }}>Translation</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {VERSIONS.map(v => (
              <button key={v} onClick={() => changeVer(v)} style={{ border: 'none', borderRadius: 999, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: SANS, background: ver === v ? GOLD : 'var(--cream)', color: 'var(--ink)' }}>{v}</button>
            ))}
          </div>
        </div>
      )}

      {blocks === null ? (
        <div style={{ fontSize: 13, color: 'var(--stone)', padding: '20px 0' }}>Loading…</div>
      ) : error ? (
        <div style={{ fontSize: 13, color: 'var(--stone)', padding: '20px 0' }}>{error}</div>
      ) : blocks.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--stone)', padding: '20px 0' }}>{day.passage_ref}</div>
      ) : blocks.map((b, i) => (
        <div key={i} style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: 'var(--lavender-ink)', marginBottom: 6 }}>{b.book} {b.chapter}:{b.start_verse}{b.end_verse !== b.start_verse ? `-${b.end_verse}` : ''}</div>
          <div style={{ fontFamily: 'var(--scripture-font)', fontSize: 'var(--scripture-size)', lineHeight: 'var(--scripture-lh)', color: 'var(--ink)' }}>
            {b.verses.map(v => (
              <span key={v.v}><sup style={{ fontSize: '0.6em', fontWeight: 700, color: 'var(--stone)', marginRight: 3 }}>{v.v}</sup>{v.t}{' '}</span>
            ))}
          </div>
        </div>
      ))}

      {lastBlock && (
        <div style={{ fontSize: 12.5, color: 'var(--stone)', marginTop: 4 }}>
          {ver} · <span style={{ color: 'var(--gold-ink)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => onOpenBible?.(lastBlock.book, lastBlock.chapter)}>Read all of {lastBlock.book} {lastBlock.chapter}</span>
        </div>
      )}
    </div>
  )
}

function DevotionalStep({ day, sermon, churchName }) {
  const [quoteOpen, setQuoteOpen] = useState(false)
  return (
    <div>
      <div style={{ fontSize: 12.5, color: 'var(--stone)', marginBottom: 14 }}>Written from Sunday's sermon · reviewed by {churchName || 'your church'}</div>
      <div style={{ fontFamily: SERIF, fontSize: 16, lineHeight: 1.65, color: 'var(--ink)', whiteSpace: 'pre-wrap' }}>{day.devotional}</div>

      {day.quote_text && (
        <div style={{ borderLeft: `3px solid ${GOLD}`, paddingLeft: 14, margin: '18px 0' }}>
          <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 16, color: 'var(--ink)', lineHeight: 1.5 }}>"{day.quote_text}"</div>
          <div style={{ fontSize: 12.5, color: 'var(--stone)', marginTop: 8 }}>
            From Sunday's sermon ·{' '}
            <button type="button" onClick={() => setQuoteOpen(o => !o)} style={{ background: 'none', border: 'none', color: 'var(--gold-ink)', fontWeight: 700, cursor: 'pointer', fontFamily: SANS, padding: 0 }}>
              ▶ {fmtClock(day.quote_s || 0)}
            </button>
          </div>
          {quoteOpen && sermon?.youtube_id && (
            <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: 14, overflow: 'hidden', background: '#000', marginTop: 10 }}>
              <YouTubeEmbed videoId={sermon.youtube_id} start={day.quote_s || 0} end={(day.quote_s || 0) + 60}/>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ReflectStep({ day, plan, userId, userChurchId, commentCount, onFinish, saving }) {
  const [draft, setDraft] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const [posting, setPosting] = useState(false)
  const [answersOpen, setAnswersOpen] = useState(false)

  async function postAnswer() {
    const body = draft.trim()
    if (!body || !userId || !userChurchId || posting) return
    setPosting(true)
    await supabase.from('reading_plan_day_comments').insert({
      plan_day_id: day.id, church_id: userChurchId, user_id: userId, text: body, private: isPrivate,
    })
    setPosting(false)
    setDraft('')
  }

  return (
    <div>
      <div style={S.thinkCard}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--lavender-ink)', marginBottom: 8 }}>Think about</div>
        <div style={{ fontFamily: SERIF, fontSize: 19, lineHeight: 1.4, color: 'var(--ink)' }}>{day.reflection}</div>
      </div>

      <textarea value={draft} onChange={e => setDraft(e.target.value)} placeholder="Write your answer… (optional)"
        style={S.answerBox}/>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button type="button" onClick={() => setIsPrivate(false)} style={{ ...S.audienceChip, ...(!isPrivate ? S.audienceChipOn : {}) }}>👥 Church</button>
        <button type="button" onClick={() => setIsPrivate(true)} style={{ ...S.audienceChip, ...(isPrivate ? S.audienceChipOn : {}) }}>🔒 Only me</button>
        {draft.trim() && (
          <button type="button" disabled={posting} onClick={postAnswer} style={{ ...S.audienceChip, marginLeft: 'auto', background: GOLD, color: 'var(--ink)', fontWeight: 800 }}>
            {posting ? '…' : 'Save answer'}
          </button>
        )}
      </div>

      {/* Peek row — faces, "n from [church] answered", See → stacked answers
          sheet. Hidden when nobody's answered (docs/FIX-PLANS.md §3a follow-up). */}
      {commentCount > 0 && (
        <button type="button" onClick={() => setAnswersOpen(true)} style={S.peekRow}>
          <PeekFaces planDayId={day.id} churchId={userChurchId}/>
          <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>{commentCount} from your church answered</div>
            <div style={{ fontSize: 12, color: 'var(--stone)' }}>Answer first, or take a look</div>
          </span>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--gold-ink)' }}>See</span>
        </button>
      )}

      <button onClick={onFinish} disabled={saving} style={{ ...S.darkBtn, marginTop: 18 }}>
        {saving ? '…' : `Finish Day ${day.day_number} ✓`}
      </button>

      <StackedSheet open={answersOpen} onClose={() => setAnswersOpen(false)} backLabel="Reflect" zIndex={450}>
        <div style={S.header}>
          <div style={{ flex: 1, fontSize: 16, fontWeight: 800, color: 'var(--ink)', textAlign: 'center' }}>Answers</div>
          <button onClick={() => setAnswersOpen(false)} aria-label="Close" style={S.iconBtn}>✕</button>
        </div>
        <div style={S.scroll}>
          {userChurchId && <PlanDayComments planDayId={day.id} churchId={userChurchId} userId={userId} placeholder={day.reflection}/>}
        </div>
      </StackedSheet>
    </div>
  )
}

// Small face-only preview for the peek row — reuses the same activity shape
// as the overview's "have finished" line but scoped to comment authors.
function PeekFaces({ planDayId, churchId }) {
  const [profiles, setProfiles] = useState([])
  useEffect(() => {
    let cancelled = false
    supabase.from('reading_plan_day_comments').select('user_id, profiles(full_name, avatar_url)')
      .eq('plan_day_id', planDayId).eq('church_id', churchId).eq('removed', false).eq('private', false)
      .limit(3)
      .then(({ data }) => { if (!cancelled) setProfiles((data || []).map(r => ({ user_id: r.user_id, ...r.profiles }))) })
    return () => { cancelled = true }
  }, [planDayId, churchId])
  if (profiles.length === 0) return null
  return <FaceStack userIds={profiles.map(p => p.user_id)} profilesById={Object.fromEntries(profiles.map(p => [p.user_id, p]))} size={28}/>
}

// ── DONE ─────────────────────────────────────────────────────────────────
function DoneScreen({ plan, day, currentDay, completedSet, activity, userChurchId, userId, onClose, onNextDay }) {
  const total = activity?.total || 0
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={S.header}>
        <button onClick={() => {}} aria-label="Back" style={{ ...S.iconBtn, visibility: 'hidden' }}/>
        <div style={S.headerTitle}>{plan.title}</div>
        <button onClick={onClose} aria-label="Close" style={S.iconBtn}>✕</button>
      </div>
      <div style={{ ...S.scroll, flex: 1 }}>
        <div style={S.doneCard}>
          <div style={S.doneCheck}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7"/></svg>
          </div>
          <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--ink)', marginTop: 10 }}>Day {currentDay} done</div>
          {total > 0 && <div style={{ fontSize: 13, color: 'var(--stone)', marginTop: 4 }}>You and {total - 1 > 0 ? total - 1 : 0} others from your church today</div>}
          <div style={{ marginTop: 16 }}><DayProgressBar completedSet={completedSet} currentDay={currentDay}/></div>
        </div>

        {userChurchId && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink)', marginBottom: 10 }}>On today's question</div>
            <PlanDayComments planDayId={day.id} churchId={userChurchId} userId={userId} placeholder={day.reflection}/>
          </div>
        )}
      </div>
      <div style={{ padding: '0 18px calc(env(safe-area-inset-bottom) + 18px)', flexShrink: 0 }}>
        <button onClick={currentDay < 5 ? onNextDay : onClose} style={S.goldBtn}>
          {currentDay < 5 ? `Day ${currentDay + 1} is ready when you are ›` : 'Plan finished 🎉'}
        </button>
      </div>
    </div>
  )
}

const S = {
  backdrop: { position: 'fixed', inset: 0, zIndex: 620, background: 'rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', fontFamily: SANS },
  sheet: { background: 'var(--cream)', width: '100%', maxWidth: 480, margin: '0 auto', height: '100%', display: 'flex', flexDirection: 'column', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)' },
  header: { flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', gap: 10 },
  headerTitle: { flex: 1, minWidth: 0, textAlign: 'center', fontSize: 13, fontWeight: 800, color: 'var(--stone)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  iconBtn: { flexShrink: 0, width: 40, height: 40, borderRadius: 20, background: 'var(--white)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 15, color: 'var(--ink)' },

  scroll: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '4px 18px calc(env(safe-area-inset-bottom) + 32px)' },
  dayTitle: { fontSize: 24, fontWeight: 800, lineHeight: 1.2, color: 'var(--ink)', margin: '0 0 16px' },

  coverCard: { background: 'var(--white)', borderRadius: 20, padding: 10, marginBottom: 16, boxShadow: '0 4px 16px rgba(31,27,22,0.08)' },
  coverCardBtn: { display: 'block', width: '100%', textAlign: 'left', background: 'var(--white)', border: 'none', borderRadius: 20, padding: 10, marginBottom: 16, cursor: 'pointer', boxShadow: '0 4px 16px rgba(31,27,22,0.08)', fontFamily: SANS },
  coverPlain: { position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: 20, overflow: 'hidden', background: 'var(--border)', marginBottom: 16 },
  coverThumbImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  coverPlainOverlay: { position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0))' },
  coverPlainLabel: { position: 'absolute', left: 14, bottom: 12, right: 14, color: '#fff', fontSize: 15, fontWeight: 800 },
  openSermonLink: { flexShrink: 0, whiteSpace: 'nowrap', background: 'none', border: '1.5px solid var(--gold)', borderRadius: 999, color: 'var(--gold-ink)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: SANS, padding: '9px 14px' },

  checklistCard: { display: 'flex', flexDirection: 'column', background: 'var(--white)', borderRadius: 20, marginBottom: 14, overflow: 'hidden' },
  checklistRow: { display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: 'none', border: 'none', padding: '14px 16px', cursor: 'pointer', fontFamily: SANS },
  checkCircle: { flexShrink: 0, width: 24, height: 24, borderRadius: 12, border: '2px solid var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  checkCircleDone: { background: PURPLE, border: 'none' },

  darkBtn: { width: '100%', textAlign: 'center', background: '#1F1B16', border: 'none', borderRadius: 999, padding: 15, fontSize: 15, fontWeight: 800, color: '#fff', cursor: 'pointer', fontFamily: SANS },
  goldBtn: { width: '100%', textAlign: 'center', background: GOLD, border: 'none', borderRadius: 999, padding: 15, fontSize: 15, fontWeight: 800, color: 'var(--ink)', cursor: 'pointer', fontFamily: SANS },
  completeBanner: { textAlign: 'center', fontFamily: SERIF, fontSize: 15, color: 'var(--gold-ink)', background: 'rgba(232,200,122,0.08)', border: '1px solid #E8C87A44', borderRadius: 12, padding: 14 },

  stepHeader: { flexShrink: 0, display: 'flex', alignItems: 'center', padding: '14px 18px 10px' },
  kicker: { flexShrink: 0, padding: '0 18px 12px', fontSize: 11.5, fontWeight: 800, letterSpacing: '0.06em', color: 'var(--lavender-ink)' },

  aaPopover: { background: 'var(--white)', borderRadius: 16, padding: 14, marginBottom: 14, boxShadow: '0 4px 16px rgba(31,27,22,0.08)' },
  aaSizeBtn: { width: 44, height: 44, borderRadius: 14, border: 'none', background: 'var(--cream)', fontFamily: 'inherit', fontSize: 15, fontWeight: 800, color: 'var(--ink)', cursor: 'pointer' },

  thinkCard: { background: 'var(--lavender)', borderRadius: 20, padding: 18, marginBottom: 16 },
  answerBox: { width: '100%', minHeight: 90, resize: 'vertical', border: '1px solid var(--border-strong)', borderRadius: 16, padding: 14, fontSize: 14, color: 'var(--ink)', background: 'var(--white)', fontFamily: SANS, outline: 'none', boxSizing: 'border-box', marginBottom: 10 },
  audienceChip: { background: 'var(--white)', border: 'none', borderRadius: 999, padding: '9px 14px', fontSize: 12.5, fontWeight: 700, color: 'var(--stone)', cursor: 'pointer', fontFamily: SANS },
  audienceChipOn: { background: 'var(--cream)', color: 'var(--ink)', boxShadow: '0 0 0 1.5px var(--border-strong)' },
  peekRow: { display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: 'var(--white)', border: 'none', borderRadius: 18, padding: '12px 14px', marginBottom: 14, cursor: 'pointer', fontFamily: SANS },

  doneCard: { background: 'var(--white)', borderRadius: 20, padding: 22, textAlign: 'center', marginBottom: 16 },
  doneCheck: { width: 44, height: 44, borderRadius: 22, background: '#1F1B16', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' },
}
